import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Virtuoso, ListRange } from 'react-virtuoso';
import { GreenroomState } from '../../stores/types/types';
import Socket from '../../connection/socket';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import type { PresentedMediaItem } from '../../stores/types/media';
import useShots from '../../contexts/shots/provider';
import * as MediaActions from '../../stores/actions/media';
import type { GreenroomUser } from '../../stores/types/session';
import useMedia from '../../hooks/use-media';

interface Props {
	socket: Socket | null;
	mediaId: number | null;
	clearMediaSelectedForSlideView: Function;
	openMediaModal: Function;
	user: GreenroomUser | null;
	isHost: boolean;
	onPresent: (mediaId: number, page: number) => void;
	stopPresenting: (mediaId: number) => void;
}

export default function MediaSlidesView(props: Props) {
	const dispatch = useDispatch();
	const {
		mediaId, clearMediaSelectedForSlideView, openMediaModal, onPresent,
	} = props;

	const {
		canEditOrControlMedia,
		canDeleteMedia,
	} = useMedia();

	const presentedMedia = useSelector((state: GreenroomState) => state.MediaReducer.presentedMedia);
	const shotStartTimestampEntity = useSelector(
		(state: GreenroomState) => state.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);

	const baseMediaItem = useSelector(
		(state: GreenroomState) => state.MediaReducer.media.find((m) => m.id === mediaId),
	);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const firstSlideIndex = useSelector((s: GreenroomState) => s.MediaReducer.slideViewMediaIndex);

	const {
		currentShot: {
			shot_data: {
				shotFeeds,
			},
		},
		isShotBuilderOpen,
	} = useShots();

	const [userCanControl, userCanEdit, userCanDelete] = useMemo(
		() => [
			canEditOrControlMedia(baseMediaItem!, props.user, props.isHost, true),
			canEditOrControlMedia(baseMediaItem!, props.user, props.isHost, false),
			canDeleteMedia(baseMediaItem!, props.user, props.isHost)],
		[props.isHost, baseMediaItem, props.user, shotFeeds],
	);

	const hasSwitchSlidesControl = userCanControl || userCanEdit || userCanDelete;

	const presentedMediaItem = useMemo(() => presentedMedia.find(
		(m) => m.mediaId === mediaId,
	), [mediaId, presentedMedia]);

	const isInCanvas = useMemo(() => shotFeeds.find(
		(f) => f.media === mediaId,
	), [mediaId, shotFeeds]);

	/* this is a duplicte fn in feed-media.tsx, media-slides-view.tsx, and video-feed-thumbnail.tsx */
	const changePage = useCallback((newPage: number) => {
		if (!mediaId || !hasSwitchSlidesControl) return;

		if (!presentedMediaItem) {
			onPresent(mediaId, newPage);
		} else {
			const newVideoState = { paused: !shotStartTimestampEntity, timeElapsed: 0, volume: 1 };
			dispatch(
				MediaActions.setPresentedMediaPage(mediaId, newPage, newVideoState),
			);
		}
	}, [mediaId,
		presentedMediaItem,
		shotStartTimestampEntity,
		hasSwitchSlidesControl,
	]);

	const onDragEnd = (result: any) => {
		const { destination, source } = result;

		if (!destination || !source || !baseMediaItem) return;

		// dropped in the same spot it started
		if (
			destination.droppableId === source.droppableId
			&& destination.index === source.index
		) return;

		const newPages = baseMediaItem.media_data.image_array;
		const movedPage = newPages.splice(source.index, 1).join();
		newPages.splice(destination.index, 0, movedPage);

		// Update current page
		const updatedPresentedMedia = presentedMedia.map((med: PresentedMediaItem) => {
			const nMed = med;
			if (nMed.mediaId === mediaId) {
				nMed.currentPage = destination.index + 1;
			}
			return nMed;
		});

		dispatch(MediaActions.setPresentedMedia(updatedPresentedMedia));

		// Add changes to mediaItem in media reducer and save to db
		if (baseMediaItem && token) {
			baseMediaItem.images = newPages.join();
			baseMediaItem.media_data.image_array = newPages;
			dispatch(MediaActions.saveMediaItem(token, baseMediaItem, undefined));
		}
	};

	const mediaPages = useMemo((): JSX.Element[] | undefined => baseMediaItem?.media_data.image_array
		.map((pageUrl: string, index: number) => {
			const url = baseMediaItem?.media_data.thumbnail_map[pageUrl] ?? pageUrl;
			// currentPage is indexed at 1
			const isCurrentPage = index === (presentedMediaItem?.currentPage ?? 1) - 1;

			return <div style={{ minHeight: '1px' }}>
			<Draggable draggableId={`${index}-draggable`} key={`${index}-draggable`} index={index}>
				{(provided) => (
					<div
						key={index}
							className={classNames('media-slide-container', { selected: isCurrentPage })}
						{...provided.draggableProps} ref={provided.innerRef} {...provided.dragHandleProps}
					>
						<div className="media-slides-handle">
							<Icon
								name={ICONS.THREE_DOTS_VERTICAL}
								color={COLORS.DEFAULT_GRAY}
								size={14}></Icon>
							{index + 1}
						</div>
						<div
							className={classNames('media-slide', {
								selected: isCurrentPage,
								'no-action': isShotBuilderOpen,
							})}
							onClick={() => changePage(index + 1)}
						>
							<div
								className={classNames('thumbnail-container', baseMediaItem?.media_data.fill && 'fill')}
								style={{ background: baseMediaItem?.media_data.background_color }}
							>
								<img src={url} alt={`slide number ${index}`} />
							</div>
								{isInCanvas && isCurrentPage
								&& <div className="presenting-badge"><div className="presenting-dot">
									</div>Presenting</div>
							}
							{!isShotBuilderOpen && hasSwitchSlidesControl && (
								<div className="media-slide-overlay">
									<div className="green-slide-button">
										Switch to slide
									</div>
								</div>
							)}
						</div>
					</div>
				)}
			</Draggable>
		</div>;
		}), [
		baseMediaItem?.media_data,
		presentedMediaItem?.currentPage,
		changePage,
		isShotBuilderOpen,
		isInCanvas,
	]);

	useEffect(() => {
		if (!mediaPages) {
			clearMediaSelectedForSlideView();
		}
	}, [mediaPages]);

	const onRangeChanged = (range: ListRange) => {
		dispatch(MediaActions.setSlideViewMediaIndex(range.startIndex));
	}

	return (
		<div className="media-slides-view">
			<div className="media-slides-header">
				<div onClick={() => clearMediaSelectedForSlideView()} className="media-slides-header-all">
					<Icon name={ICONS.ARROW_LEFT} color={COLORS.WHITE} size={16}></Icon>
					<span>All</span>
				</div>

				<div onClick={() => openMediaModal(mediaId)} className="media-slides-header-preview">
					<Icon name={ICONS.FULLSCREEN} color={COLORS.PALE_GREEN} size={16}></Icon>
					<span>Preview</span>
				</div>
			</div>
			<div className="media-slides-pages-view">
				{mediaPages && (
					<div className="virtuoso-scroller">
						<DragDropContext onDragEnd={onDragEnd}>
							<Droppable droppableId={'media-slides-container'}>
								{(provided) => (
									<div ref={provided.innerRef} {...provided.droppableProps}>
										<Virtuoso
											rangeChanged={onRangeChanged}
											style={{ height: '100%' }}
											totalCount={mediaPages?.length}
											itemContent={(index) => mediaPages?.[index] ?? <div style={{ minHeight: '1px' }}/>}
											initialTopMostItemIndex={firstSlideIndex}
										/>
										{provided.placeholder}
									</div>
								)}
							</Droppable>
						</DragDropContext>
					</div>
				)}
			</div>
		</div>
	);
}
